import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Package, Truck, Store, User, CreditCard, ClipboardList, History,
  Loader2, ExternalLink, MailCheck, Ban, RefreshCw, FileText, Save, Printer, FileDown,
} from "lucide-react";
import { api, API, money, etTime, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS = ["new", "processing", "ready_for_pickup", "fulfilled", "cancelled"];
const PAYMENT = ["pending", "paid", "unpaid"];
const LABELS = { ready_for_pickup: "Ready for Pickup" };
const lbl = (s) => LABELS[s] || s;

const Section = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-navy/10 p-5">
    <div className="flex items-center gap-2 font-extrabold text-navy text-sm uppercase tracking-wide mb-4">
      <Icon className="w-4 h-4 text-brick" /> {title}
    </div>
    {children}
  </div>
);

const Row = ({ k, v, mono }) => (
  <div className="flex justify-between gap-4 py-1 text-sm border-b border-navy/5 last:border-0">
    <span className="text-ink/60">{k}</span>
    <span className={`text-navy font-semibold text-right ${mono ? "font-mono text-xs break-all" : ""}`}>{v ?? "—"}</span>
  </div>
);

export default function AdminOrderDetail() {
  const { orderNumber } = useParams();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState("");
  const [rates, setRates] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/orders/${orderNumber}`).then((r) => { setO(r.data); setNotes(r.data.admin_notes || ""); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load order"));
  }, [orderNumber]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const update = (field, value) => act(async () => {
    await api.patch(`/admin/orders/${orderNumber}`, { [field]: value });
    toast.success("Order updated");
  }, field);

  const saveNotes = () => act(async () => {
    await api.patch(`/admin/orders/${orderNumber}`, { admin_notes: notes });
    toast.success("Notes saved");
  }, "notes");

  const getRates = () => act(async () => {
    const r = await api.post(`/admin/orders/${orderNumber}/shipping-rates`);
    setRates(r.data.rates || []);
    setSelectedRate(r.data.rates?.[0] || null);
    if (!r.data.rates?.length) toast.info("No rates returned for this address.");
  }, "rates");

  const buyLabel = () => act(async () => {
    await api.post(`/admin/orders/${orderNumber}/buy-label`, {
      rate_id: selectedRate.rate_id, carrier: selectedRate.carrier,
      service: selectedRate.service, amount: selectedRate.amount,
    });
    setRates(null);
    toast.success("Label purchased & tracking emailed");
  }, "buy");

  const viewLabel = () => act(async () => {
    const r = await api.get(`/admin/orders/${orderNumber}/label`);
    if (r.data.label_url) window.open(r.data.label_url, "_blank");
    else toast.error("No label URL available");
  }, "view");

  if (!o) return <div className="p-8 text-ink/60">Loading order…</div>;

  const ship = o.shipping_address || {};
  const isPickup = o.fulfillment === "pickup";
  const hasLabel = !!o.shippo_label_url;

  return (
    <div className="space-y-6" data-testid="admin-order-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin/orders" data-testid="back-to-orders" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-brick">
          <ArrowLeft className="w-4 h-4" /> All orders
        </Link>
        <div className="flex items-center gap-2 text-xs text-ink/50">
          All times shown in <b>US Eastern</b> · stored as UTC
        </div>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy" data-testid="detail-order-number">{o.order_number}</h1>
          <p className="text-ink/60 text-sm mt-1">Created {etTime(o.created_at)}</p>
        </div>
        <div className="flex items-center gap-3">
          {o.fulfillment === "pickup" && o.payment_status === "paid" && !["picked_up", "cancelled", "fulfilled"].includes(o.status) && (
            <>
              <button data-testid="detail-pickup-notice"
                onClick={async () => {
                  try {
                    await api.post(`/admin/orders/${orderNumber}/pickup-ready-notice`);
                    toast.success("Pickup-ready notice emailed to the customer");
                    load();
                  } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Could not send notice");
                  }
                }}
                className="inline-flex items-center gap-2 text-xs font-bold bg-navy text-cream rounded-md px-3 py-2 hover:bg-teal-700">
                {o.pickup_ready_emailed ? "Resend Pickup Notice" : "Send Pickup Ready Notice"}
              </button>
              <button data-testid="detail-mark-picked-up"
              onClick={async () => {
                if (!window.confirm("Mark this order as picked up? The item(s) will be marked SOLD.")) return;
                try {
                  await api.patch(`/admin/orders/${orderNumber}`, { status: "picked_up" });
                  toast.success("Order marked as picked up");
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail));
                }
              }}
              className="inline-flex items-center gap-2 text-xs font-bold bg-green-600 text-white rounded-md px-3 py-2 hover:bg-green-700">
              Mark as Picked Up
            </button>
            </>
          )}
          <Link to={`/admin/orders/${orderNumber}/packing-slip`} data-testid="btn-packing-slip"
            className="inline-flex items-center gap-2 text-xs font-bold bg-navy text-white rounded-md px-3 py-2">
            <Printer className="w-3.5 h-3.5" /> Print Packing Slip
          </Link>
          <a href={`${API}/admin/orders/${orderNumber}/packing-slip.pdf`} data-testid="btn-packing-slip-pdf"
            className="inline-flex items-center gap-2 text-xs font-bold bg-navy/10 text-navy rounded-md px-3 py-2">
            <FileDown className="w-3.5 h-3.5" /> Download PDF
          </a>
          <span data-testid="detail-status-badge" className="text-xs font-bold rounded-full px-3 py-1 bg-teal-100 text-teal-700 uppercase">{lbl(o.status)}</span>
          <span className="text-xs font-bold rounded-full px-3 py-1 bg-amber-100 text-amber-700 uppercase">{o.payment_status}</span>
          <span className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-1 bg-navy/10 text-navy">
            {isPickup ? <Store className="w-3 h-3" /> : <Truck className="w-3 h-3" />} {isPickup ? "Local Pickup" : "Shipping"}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Status controls + timeline */}
        <Section icon={ClipboardList} title="Status & Fulfillment" testid="section-status">
          <div className="flex flex-wrap gap-4 mb-4">
            <label className="text-sm">
              <span className="block text-xs font-bold text-navy uppercase mb-1">Order status</span>
              <select data-testid="detail-status-select" value={o.status} onChange={(e) => update("status", e.target.value)}
                className="rounded-md border border-navy/20 px-3 py-2 bg-white">
                {STATUS.filter((s) => s !== "ready_for_pickup" || isPickup).map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs font-bold text-navy uppercase mb-1">Payment status</span>
              <select data-testid="detail-payment-select" value={o.payment_status} onChange={(e) => update("payment_status", e.target.value)}
                className="rounded-md border border-navy/20 px-3 py-2 bg-white">
                {PAYMENT.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <div className="text-xs text-ink/60 space-y-1">
            {[["Checkout completed", o.checkout_completed_at], ["Payment completed", o.paid_at],
              ["Label purchased", o.label_purchased_at], ["Shipped", o.shipped_at],
              ["Delivered", o.delivered_at], ["Cancelled", o.cancelled_at],
              ["Refunded", o.refunded_at], ["Last updated", o.updated_at]]
              .filter(([, v]) => v).map(([k, v]) => <div key={k} className="flex justify-between"><span>{k}</span><b className="text-navy">{etTime(v)}</b></div>)}
          </div>
        </Section>

        {/* Customer */}
        <Section icon={User} title="Customer (admin-only)" testid="section-customer">
          <Row k="Name" v={o.customer?.name} />
          <Row k="Email" v={o.customer?.email} />
          <Row k="Phone" v={o.customer?.phone || "—"} />
          {!isPickup && <>
            <Row k="Ship to" v={`${ship.line1}${ship.line2 ? " " + ship.line2 : ""}`} />
            <Row k="City/State/ZIP" v={`${ship.city}, ${ship.state} ${ship.postal_code}`} />
            <Row k="Country" v={ship.country} />
          </>}
          {isPickup && <Row k="Fulfillment" v="Local Pickup — see pickup email/address" />}
          {o.note && <Row k="Customer note" v={o.note} />}
        </Section>

        {/* Items */}
        <Section icon={Package} title="Items (snapshot at purchase)" testid="section-items">
          <div className="space-y-3">
            {o.items.map((it, i) => (
              <div key={it.product_id + "-" + i} data-testid={`detail-item-${i}`} className="flex gap-3 border-b border-navy/5 pb-3 last:border-0">
                {it.image && <img src={it.image} alt={it.name} className="w-14 h-14 rounded object-cover border border-navy/10" />}
                <div className="flex-1 text-sm">
                  <div className="font-bold text-navy">{it.name}</div>
                  <div className="text-ink/60 text-xs">
                    Qty {it.quantity} × {money(it.price)} = <b className="text-navy">{money(it.price * it.quantity)}</b>
                  </div>
                  <div className="text-ink/50 text-xs mt-1">
                    {it.inventory_id && <span className="font-mono font-bold text-navy">{it.inventory_id} · </span>}
                    {it.sku && <span>SKU {it.sku} · </span>}
                    {it.condition && <span>{it.condition} · </span>}
                    {it.is_dropship ? "Supplier-fulfilled" : "Personal inventory"}
                    {it.weight_oz ? ` · ${it.weight_oz}oz` : ""}
                    {it.stock_before != null ? ` · stock ${it.stock_before}→${it.stock_after}` : ""}
                  </div>
                  {it.condition_details && <div className="text-ink/50 text-xs italic mt-0.5">{it.condition_details}</div>}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Financials */}
        <Section icon={CreditCard} title="Payment & Financials" testid="section-financials">
          <Row k="Subtotal" v={money(o.subtotal)} />
          {o.discount > 0 && <Row k="Discount" v={`- ${money(o.discount)}`} />}
          {o.tax > 0 && <Row k="Tax" v={money(o.tax)} />}
          <Row k="Shipping" v={money(o.shipping_cost)} />
          <Row k="Total charged" v={<b>{money(o.total)}</b>} />
          {o.refund_amount > 0 && <Row k="Refunded" v={money(o.refund_amount)} />}
          <Row k="Currency" v={(o.currency || "USD").toUpperCase()} />
          <Row k="Payment method" v={o.payment_provider || o.payment_method} />
          <Row k="Payment status" v={o.payment_status} />
          <Row k="Provider txn / intent" v={o.payment_intent_id} mono />
          {(o.card_brand || o.card_last4) && <Row k="Card" v={`${o.card_brand || ""} •••• ${o.card_last4 || ""}`} />}
          {o.refund_reference && <Row k="Refund reference" v={o.refund_reference} mono />}
          {o.refund_needed && <div className="mt-2 text-xs font-bold text-brick">⚠ Refund due (cancelled paid order)</div>}
          {o.payment_error && <div className="mt-2 text-xs text-brick">{o.payment_error}</div>}
        </Section>

        {/* Shipping / Shippo */}
        <Section icon={Truck} title="Shipping & Label" testid="section-shipping">
          {isPickup ? (
            <div className="text-sm text-ink/70 flex items-center gap-2"><Store className="w-4 h-4" /> Local Pickup — no shipping label required.</div>
          ) : o.has_dropship ? (
            <div className="text-sm text-ink/70 flex items-center gap-2"><Truck className="w-4 h-4" /> Supplier-fulfilled (CJ) — ships from supplier.</div>
          ) : (
            <>
              {hasLabel ? (
                <>
                  <Row k="Carrier / Service" v={`${o.shippo_carrier || ""} ${o.shippo_service || ""}`} />
                  <Row k="Label cost" v={o.shippo_rate_amount != null ? money(o.shippo_rate_amount) : "—"} />
                  <Row k="Package" v={`${o.shippo_package_weight_oz || "?"}oz · ${o.shippo_package_dims || ""}`} />
                  <Row k="Tracking #" v={o.shippo_tracking_number} mono />
                  <Row k="Tracking status" v={o.shippo_tracking_status || "—"} />
                  <Row k="ETA" v={o.shippo_eta ? etTime(o.shippo_eta, false) : "—"} />
                  <Row k="Shippo txn ID" v={o.shippo_transaction_id} mono />
                  <Row k="Void status" v={o.shippo_void_status || "—"} />
                  <Row k="Tracking emails sent" v={o.tracking_emailed_count} />
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button data-testid="btn-view-label" onClick={viewLabel} disabled={busy === "view"}
                      className="inline-flex items-center gap-1 text-xs font-bold bg-navy text-white rounded-md px-3 py-2 disabled:opacity-60">
                      {busy === "view" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} View / Print label
                    </button>
                    {o.shippo_tracking_url && <a data-testid="btn-track" href={o.shippo_tracking_url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold bg-navy/10 text-navy rounded-md px-3 py-2"><ExternalLink className="w-3 h-3" /> Track</a>}
                    <button data-testid="btn-resend-tracking" onClick={() => act(async () => { await api.post(`/admin/orders/${orderNumber}/resend-tracking`); toast.success("Tracking email resent"); }, "resend")}
                      disabled={busy === "resend"} className="inline-flex items-center gap-1 text-xs font-bold bg-navy/10 text-navy rounded-md px-3 py-2 disabled:opacity-60">
                      <MailCheck className="w-3 h-3" /> Resend tracking
                    </button>
                    <button data-testid="btn-refresh-tracking" onClick={() => act(async () => { await api.post(`/admin/orders/${orderNumber}/refresh-tracking`); toast.success("Tracking refreshed"); }, "refresh")}
                      disabled={busy === "refresh"} className="inline-flex items-center gap-1 text-xs font-bold bg-navy/10 text-navy rounded-md px-3 py-2 disabled:opacity-60">
                      <RefreshCw className="w-3 h-3" /> Refresh status
                    </button>
                    {o.shippo_void_status !== "refunded" && <button data-testid="btn-void-label"
                      onClick={() => act(async () => { await api.post(`/admin/orders/${orderNumber}/void-label`); toast.success("Void/refund requested"); }, "void")}
                      disabled={busy === "void"} className="inline-flex items-center gap-1 text-xs font-bold bg-brick/10 text-brick rounded-md px-3 py-2 disabled:opacity-60">
                      <Ban className="w-3 h-3" /> Void label
                    </button>}
                  </div>
                </>
              ) : o.payment_status !== "paid" ? (
                <div className="text-sm text-ink/60">Available once the order is paid.</div>
              ) : rates === null ? (
                <button data-testid="btn-get-rates" onClick={getRates} disabled={busy === "rates"}
                  className="inline-flex items-center gap-2 text-sm font-bold bg-navy text-white rounded-md px-4 py-2 disabled:opacity-60">
                  {busy === "rates" && <Loader2 className="w-4 h-4 animate-spin" />} Get USPS rates
                </button>
              ) : (
                <div className="flex flex-wrap items-end gap-2">
                  <select data-testid="detail-rate-select" value={selectedRate?.rate_id || ""}
                    onChange={(e) => setSelectedRate(rates.find((r) => r.rate_id === e.target.value))}
                    className="rounded-md border border-navy/20 px-3 py-2 text-sm bg-white">
                    {rates.map((r) => <option key={r.rate_id} value={r.rate_id}>{r.carrier} {r.service} — {money(r.amount)}{r.estimated_days ? ` (${r.estimated_days}d)` : ""}</option>)}
                  </select>
                  <button data-testid="btn-buy-label" onClick={buyLabel} disabled={busy === "buy" || !selectedRate}
                    className="inline-flex items-center gap-2 text-sm font-bold bg-brick text-white rounded-md px-4 py-2 disabled:opacity-60">
                    {busy === "buy" && <Loader2 className="w-4 h-4 animate-spin" />} Buy label
                  </button>
                </div>
              )}
              <div className="text-xs text-ink/50 mt-3">Ships from: {o.shippo_carrier ? "" : "300 Sand Iron Circle Unit 334, Ponte Vedra Beach, FL 32082"}</div>
            </>
          )}
        </Section>

        {/* Admin notes */}
        <Section icon={ClipboardList} title="Private Admin Notes" testid="section-notes">
          <textarea data-testid="admin-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
            placeholder="Internal notes (not visible to the customer)…"
            className="w-full rounded-md border border-navy/20 px-3 py-2 text-sm" />
          <button data-testid="btn-save-notes" onClick={saveNotes} disabled={busy === "notes"}
            className="mt-2 inline-flex items-center gap-2 text-sm font-bold bg-navy text-white rounded-md px-4 py-2 disabled:opacity-60">
            {busy === "notes" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save notes
          </button>
        </Section>
      </div>

      {/* Identity + audit/history full width */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section icon={Package} title="Order Identity" testid="section-identity">
          <Row k="Order number" v={o.order_number} />
          <Row k="Internal ID" v={o.id} mono />
          <Row k="Created" v={etTime(o.created_at)} />
          <Row k="Time zone" v="US Eastern (stored UTC)" />
          {o.cj_order_number && <Row k="CJ order #" v={o.cj_order_number} mono />}
        </Section>

        <Section icon={History} title="Status History & Audit Trail" testid="section-audit">
          <div className="text-xs font-bold text-navy uppercase mb-2">Status timeline</div>
          <div className="space-y-1 mb-4 max-h-56 overflow-auto" data-testid="status-history">
            {(o.status_history || []).slice().reverse().map((h, i) => (
              <div key={`${h.at}-${h.status}-${i}`} className="flex justify-between text-xs border-b border-navy/5 py-1">
                <span className="font-semibold text-navy">{lbl(h.status)}</span>
                <span className="text-ink/50">{etTime(h.at)} · {h.by}</span>
              </div>
            ))}
            {!(o.status_history || []).length && <div className="text-xs text-ink/40">No history yet.</div>}
          </div>
          <div className="text-xs font-bold text-navy uppercase mb-2">Change log</div>
          <div className="space-y-1 max-h-56 overflow-auto" data-testid="audit-log">
            {(o.audit_log || []).slice().reverse().map((a, i) => (
              <div key={`${a.at}-${a.field}-${i}`} className="text-xs border-b border-navy/5 py-1">
                <span className="text-ink/50">{etTime(a.at)} · {a.by}</span>
                <span className="text-navy"> — {a.field}: <b>{String(a.old ?? "—")}</b> → <b>{String(a.new ?? "—")}</b></span>
              </div>
            ))}
            {!(o.audit_log || []).length && <div className="text-xs text-ink/40">No changes recorded yet.</div>}
          </div>
        </Section>
      </div>
    </div>
  );
}
